<script setup lang="ts">
import { ref } from 'vue'

import { convertSizePixels } from '@/ui-library/helpers/ui'

const props = withDefaults(
  defineProps<{
    direction?: 'rtl' | 'ltr' | 'ttb' | 'btt'
    destroyOnClose?: boolean
    lockScroll?: boolean
    showClose?: boolean
    withHeader?: boolean
    size?: number | string
    isResizable?: boolean
  }>(),
  {
    direction: 'rtl',
    destroyOnClose: true,
    lockScroll: true,
    showClose: true,
    withHeader: false,
    size: '30%',
    isResizable: false,
  },
)
const value = defineModel<boolean>('modelValue', { default: false })
const emits = defineEmits(['closed'])
const resizing = ref(false)
const DEFAULT_SIDEBAR_WIDTH = 80
const MIN_DRAWER_WIDTH = 300

const size = ref(convertSizePixels(props.size, window.innerWidth))

const onMouseMove = (moveEvent: MouseEvent) => {
  if (!resizing.value) {
    return
  }

  size.value = Math.max(MIN_DRAWER_WIDTH, Math.min(window.innerWidth - DEFAULT_SIDEBAR_WIDTH, window.innerWidth - moveEvent.clientX))
}

const onMouseUp = () => {
  resizing.value = false
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
}

const startResize = () => {
  resizing.value = true
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}
</script>
<template>
  <el-drawer v-bind="props" v-model="value" :size="size" :class="{ drawer__resizing: resizing }" @closed="emits('closed')">
    <template #header><slot name="header" /></template>
    <template #default>
      <slot name="default" />
      <div v-if="isResizable" class="resize-handle" @mousedown="startResize" />
    </template>
  </el-drawer>
</template>
<style lang="scss">
.el-drawer {
  --el-drawer-padding-primary: 24px;

  &.drawer__resizing {
    cursor: ew-resize;
    transition: none;
    user-select: none;
  }

  &.rtl {
    padding-top: 72px;
  }
}
.el-drawer__header {
  margin-bottom: 0;
}
</style>
<style lang="scss" scoped>
.resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  cursor: ew-resize;
  background: rgba(0, 0, 0, 0.1);
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}
</style>
